/**
 * Every sound in the game.
 *
 * Two layers. Underneath, the oscillators the game was born with: a crack of
 * noise and a pitch drop for a shot, a sweep for a boom, a drone for the
 * engine. On top, when they have loaded, real recordings from
 * Freesound (see `credits.ts`), one per thing the game says. A recording plays with a
 * touch of random pitch so ten shots are ten shots and not one loop, and the
 * synthesized layer stays underneath it at low volume for the thump the
 * recording usually lacks. Everything runs through one compressor, so a
 * boom on top of three shots does not clip and everything sits together.
 *
 * The context is a module singleton because a page can be built and torn
 * down many times, and browsers cap how many contexts a document may have.
 * It is created on the first gesture, the Play button, since one made
 * before that starts suspended and never recovers on iOS. Files are fetched
 * then too, so the title screen costs nothing.
 */

const STORE = "paper-tank.sound";

type Ctx = AudioContext & { resume(): Promise<void> };

let ctx: Ctx | null = null;
/** Effects: through the compressor, so a boom over three shots does not clip. */
let master: GainNode | null = null;
/** Music: straight out, so the effects never push it down. */
let musicBus: GainNode | null = null;
let noise: AudioBuffer | null = null;
const samples = new Map<string, AudioBuffer>();
let loading = false;

const AC = (): typeof AudioContext | undefined =>
  typeof window === "undefined"
    ? undefined
    : window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

/** Two seconds of white noise, shared by every crack and boom. */
const noiseBuffer = (c: AudioContext): AudioBuffer => {
  if (noise) return noise;
  const buf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noise = buf;
  return buf;
};

export type SoundName =
  | "fire"
  | "heavy"
  | "hit"
  | "ricochet"
  | "crack"
  | "boom"
  | "pickup"
  | "hurt"
  | "clear"
  | "over"
  | "start"
  | "select"
  | "unlock"
  | "stack"
  | "repair"
  | "shield"
  | "open"
  | "enemy"
  | "alert"
  | "spray"
  | "marker"
  | "alarm"
  | "crush";

/**
 * How each recording is played: how loud against the synth, how much the
 * pitch wanders, how much of the synth stays underneath, and how many may
 * sound at once before the oldest is cut (three shells landing in one step
 * should be one thud, not a chord).
 */
const MIX: Record<SoundName, { gain: number; vary: number; synth: number; trim?: number }> = {
  fire: { gain: 0.7, vary: 0.08, synth: 0.45 },
  heavy: { gain: 0.9, vary: 0.05, synth: 0.6 },
  hit: { gain: 0.5, vary: 0.14, synth: 0.2, trim: 0.5 },
  ricochet: { gain: 0.45, vary: 0.12, synth: 0.3, trim: 0.4 },
  crack: { gain: 0.6, vary: 0.12, synth: 0.25, trim: 0.5 },
  boom: { gain: 1, vary: 0.07, synth: 0.7 },
  pickup: { gain: 0.6, vary: 0.02, synth: 0.2 },
  hurt: { gain: 0.8, vary: 0.06, synth: 0.3, trim: 0.9 },
  clear: { gain: 0.7, vary: 0, synth: 0 },
  over: { gain: 0.7, vary: 0, synth: 0.2 },
  start: { gain: 0.6, vary: 0.04, synth: 0.3, trim: 0.5 },
  select: { gain: 0.5, vary: 0.05, synth: 0, trim: 0.35 },
  unlock: { gain: 0.7, vary: 0, synth: 0 },
  stack: { gain: 0.75, vary: 0.03, synth: 0.3 },
  repair: { gain: 0.7, vary: 0.03, synth: 0.2 },
  shield: { gain: 0.6, vary: 0.05, synth: 0.3, trim: 0.9 },
  open: { gain: 0.7, vary: 0, synth: 0.2 },
  enemy: { gain: 0.8, vary: 0.1, synth: 0.5, trim: 0.6 },
  alert: { gain: 0.45, vary: 0.06, synth: 0, trim: 0.3 },
  spray: { gain: 0.6, vary: 0.1, synth: 0.3, trim: 0.7 },
  marker: { gain: 0.55, vary: 0.12, synth: 0.2, trim: 0.5 },
  alarm: { gain: 0.35, vary: 0.04, synth: 0.3, trim: 2.4 },
  crush: { gain: 0.8, vary: 0.1, synth: 0.5, trim: 0.7 },
};

const FILES: string[] = [...Object.keys(MIX), "engine"];

export type MusicName = "calm" | "drive" | "tense" | "industrial" | "boss";

/**
 * Music sits under everything else, and the tracks do not arrive at the same
 * level: measured mean volumes ran from -12.3 dB to -19.6 dB, which is the
 * difference between a soundtrack and a rumour. These trims bring each to
 * about -15 dB so no yard sounds like the music forgot to start.
 */
const MUSIC_GAIN = 0.4;
const MUSIC_TRIM: Record<MusicName, number> = {
  calm: 1.7,
  drive: 1.2,
  tense: 1.65,
  industrial: 0.73,
  boss: 1.06,
};

export class Sound {
  private engineGain: GainNode | null = null;
  private engineOsc: OscillatorNode | null = null;
  private engineSub: OscillatorNode | null = null;
  private engineLoop: AudioBufferSourceNode | null = null;
  private engineLoopGain: GainNode | null = null;
  private musicSrc: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  /** What the game has asked for, and what is actually playing. These are
   *  two different things until the first click builds an audio context. */
  private musicWanted: MusicName | null = null;
  private musicName: MusicName | null = null;
  /** The two sliders, 0 to 1. Music sits above the effects by default. */
  musicVolume = readLevel(MUSIC_LEVEL, readMusic() ? 0.85 : 0);
  sfxVolume = readLevel(SFX_LEVEL, 0.7);
  private stopped = false;
  /** The last time each sound played, so a flurry is not a wall. */
  private recent = new Map<SoundName, number>();
  muted: boolean;
  /**
   * Effects off, music on. The attract loop plays a yard behind the title
   * sheet, and a menu that fires a cannon at you is a menu nobody wants.
   */
  quiet = false;

  constructor() {
    this.muted = read();
  }

  /** Music at zero is music off. */
  get musicOn(): boolean {
    return this.musicVolume > 0;
  }

  /** Call from inside a click. Returns false where there is no audio at all. */
  unlock(): boolean {
    const Ctor = AC();
    if (!Ctor) return false;
    if (!ctx) {
      ctx = new Ctor() as Ctx;
      // One compressor for the effects: loud things stay loud, but a boom
      // over three shots does not clip, and quiet things sit up with them.
      // The music does not go through it. It used to, and every explosion
      // pushed the whole soundtrack down with it.
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.knee.value = 18;
      comp.ratio.value = 4;
      comp.attack.value = 0.004;
      comp.release.value = 0.16;
      master = ctx.createGain();
      master.gain.value = curve(this.sfxVolume) * 0.9;
      master.connect(comp);
      comp.connect(ctx.destination);
      musicBus = ctx.createGain();
      musicBus.gain.value = curve(this.musicVolume);
      musicBus.connect(ctx.destination);
    }
    void ctx.resume().catch(() => {});
    this.load(ctx);
    // Whatever was asked for while there was nowhere to play it can start now.
    this.applyMusic();
    return true;
  }

  /** Fetch every recording once. Anything missing just leaves the synth. */
  private load(c: AudioContext): void {
    if (loading) return;
    loading = true;
    for (const name of FILES) {
      if (samples.has(name)) continue;
      fetch(`/sounds/${name}.mp3`)
        .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(String(res.status)))))
        .then((bytes) => c.decodeAudioData(bytes))
        .then((buffer) => samples.set(name, buffer))
        .catch(() => {
          // No file, or no decoder for it. The oscillators cover it.
        });
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    write(muted);
    if (muted) this.stopEngine();
    this.applyMusic();
  }

  /** The music slider. Down to zero stops the track; back up starts it. */
  setMusicVolume(v: number): void {
    this.musicVolume = clamp01(v);
    writeLevel(MUSIC_LEVEL, this.musicVolume);
    if (ctx && musicBus) musicBus.gain.setTargetAtTime(curve(this.musicVolume), ctx.currentTime, 0.03);
    this.applyMusic();
  }

  /** The effects slider. */
  setSfxVolume(v: number): void {
    this.sfxVolume = clamp01(v);
    writeLevel(SFX_LEVEL, this.sfxVolume);
    if (ctx && master) master.gain.setTargetAtTime(curve(this.sfxVolume) * 0.9, ctx.currentTime, 0.03);
    if (this.sfxVolume === 0) this.stopEngine();
  }

  /**
   * A loop for the level, or nothing. Fades the last one out and the next
   * one in; asking for the one already playing is a no-op, so this can be
   * called freely on every state change. Loads on demand, so a mood only
   * costs its download once it is heard.
   */
  music(name: MusicName | null): void {
    this.musicWanted = name;
    this.applyMusic();
  }

  /**
   * Play whatever is wanted, if there is anywhere to play it. Asking for the
   * one already playing does nothing, so this is safe to call on every state
   * change; it is called again from `unlock`, because until the first
   * click there is no context and the answer is always nothing.
   */
  private applyMusic(): void {
    const name = this.musicWanted;
    const io = this.out();
    if (!io || !musicBus || !name || !this.musicOn) {
      this.musicName = null;
      this.stopMusic();
      return;
    }
    if (name === this.musicName) return;
    this.musicName = name;
    const { c } = io;
    const bus = musicBus;
    const start = (buffer: AudioBuffer) => {
      if (this.musicWanted !== name || this.stopped) return;
      this.stopMusic();
      const src = c.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      const vol = c.createGain();
      vol.gain.setValueAtTime(0.0001, c.currentTime);
      vol.gain.exponentialRampToValueAtTime(MUSIC_GAIN * (MUSIC_TRIM[name] ?? 1), c.currentTime + 1.6);
      src.connect(vol);
      vol.connect(bus);
      src.start();
      this.musicSrc = src;
      this.musicGain = vol;
    };
    const key = `music-${name}`;
    const have = samples.get(key);
    if (have) {
      start(have);
      return;
    }
    fetch(`/sounds/${key}.mp3`)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(String(res.status)))))
      .then((bytes) => c.decodeAudioData(bytes))
      .then((buffer) => {
        samples.set(key, buffer);
        start(buffer);
      })
      .catch(() => {
        // No music for this mood. Silence is a mood too.
      });
  }

  private stopMusic(): void {
    const src = this.musicSrc;
    const vol = this.musicGain;
    this.musicSrc = null;
    this.musicGain = null;
    if (!src || !vol || !ctx) return;
    const now = ctx.currentTime;
    vol.gain.cancelScheduledValues(now);
    vol.gain.setValueAtTime(Math.max(0.0001, vol.gain.value), now);
    vol.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    try {
      src.stop(now + 1.25);
    } catch {
      // Already stopped.
    }
  }

  private out(): { c: AudioContext; g: GainNode } | null {
    if (this.muted || this.stopped || !ctx || !master) return null;
    return { c: ctx, g: master };
  }

  play(name: SoundName, gain = 1): void {
    if (this.quiet || this.sfxVolume === 0) return;
    const io = this.out();
    if (!io) return;
    const { c, g } = io;
    const t = c.currentTime;
    const mix = MIX[name];

    // The same sound within a few milliseconds is the same event.
    const last = this.recent.get(name) ?? -1;
    if (t - last < 0.035) return;
    this.recent.set(name, t);

    const sample = samples.get(name);
    if (sample) {
      const src = c.createBufferSource();
      src.buffer = sample;
      src.playbackRate.value = 1 + (Math.random() * 2 - 1) * mix.vary;
      const vol = c.createGain();
      vol.gain.value = mix.gain * gain;
      src.connect(vol);
      vol.connect(g);
      src.start(t);
      if (mix.trim) {
        // Fade out rather than cut, so a trimmed recording has no click.
        vol.gain.setValueAtTime(mix.gain * gain, t + mix.trim - 0.06);
        vol.gain.exponentialRampToValueAtTime(0.0001, t + mix.trim);
        src.stop(t + mix.trim + 0.01);
      }
      if (mix.synth <= 0) return;
      gain *= mix.synth;
    }

    switch (name) {
      case "fire": {
        // A crack of noise over a short pitch drop: the report, then the thump.
        burst(c, g, t, { duration: 0.18, type: "highpass", from: 1800, to: 400, gain: 0.35 * gain });
        tone(c, g, t, { wave: "sine", from: 180, to: 42, duration: 0.28, gain: 0.5 * gain });
        break;
      }
      case "heavy": {
        burst(c, g, t, { duration: 0.3, type: "lowpass", from: 1200, to: 200, gain: 0.4 * gain });
        tone(c, g, t, { wave: "sine", from: 120, to: 30, duration: 0.5, gain: 0.7 * gain });
        break;
      }
      case "hit":
        tone(c, g, t, { wave: "square", from: 420, to: 180, duration: 0.09, gain: 0.16 * gain });
        break;
      case "ricochet":
        tone(c, g, t, { wave: "triangle", from: 1400, to: 300, duration: 0.16, gain: 0.14 * gain });
        break;
      case "crack":
        burst(c, g, t, { duration: 0.14, type: "bandpass", from: 2600, to: 900, gain: 0.28 * gain, q: 1.4 });
        break;
      case "boom": {
        burst(c, g, t, { duration: 0.7, type: "lowpass", from: 1600, to: 120, gain: 0.55 * gain });
        tone(c, g, t, { wave: "sine", from: 120, to: 28, duration: 0.75, gain: 0.6 * gain });
        break;
      }
      case "pickup":
        [0, 0.07, 0.14].forEach((d, i) =>
          tone(c, g, t + d, {
            wave: "triangle",
            from: 520 * (1 + i * 0.26),
            to: 560 * (1 + i * 0.26),
            duration: 0.12,
            gain: 0.22 * gain,
          }),
        );
        break;
      case "hurt":
        tone(c, g, t, { wave: "sawtooth", from: 260, to: 70, duration: 0.3, gain: 0.3 * gain });
        break;
      case "clear":
      case "unlock":
      case "stack":
      case "repair":
      case "open":
        [392, 523, 659, 784].forEach((f, i) =>
          tone(c, g, t + i * 0.1, { wave: "triangle", from: f, to: f, duration: 0.24, gain: 0.2 * gain }),
        );
        break;
      case "shield":
        tone(c, g, t, { wave: "triangle", from: 300, to: 620, duration: 0.22, gain: 0.18 * gain });
        break;
      case "enemy":
        burst(c, g, t, { duration: 0.32, type: "lowpass", from: 900, to: 140, gain: 0.4 * gain });
        break;
      case "alarm":
        [0, 0.3, 0.6, 0.9].forEach((d, i) =>
          tone(c, g, t + d, { wave: "square", from: i % 2 ? 880 : 1180, to: i % 2 ? 880 : 1180, duration: 0.22, gain: 0.06 * gain }),
        );
        break;
      case "crush":
        burst(c, g, t, { duration: 0.35, type: "bandpass", from: 1800, to: 300, gain: 0.4 * gain, q: 1.2 });
        tone(c, g, t, { wave: "sawtooth", from: 160, to: 40, duration: 0.3, gain: 0.3 * gain });
        break;
      case "spray":
        burst(c, g, t, { duration: 0.4, type: "highpass", from: 3000, to: 2200, gain: 0.18 * gain });
        break;
      case "marker":
        tone(c, g, t, { wave: "sawtooth", from: 1800, to: 2600, duration: 0.12, gain: 0.05 * gain });
        break;
      case "alert":
        tone(c, g, t, { wave: "square", from: 880, to: 1200, duration: 0.07, gain: 0.08 * gain });
        break;
      case "over":
        [330, 262, 208, 156].forEach((f, i) =>
          tone(c, g, t + i * 0.16, { wave: "sawtooth", from: f, to: f * 0.94, duration: 0.4, gain: 0.2 * gain }),
        );
        break;
      case "start":
        [262, 392, 523].forEach((f, i) =>
          tone(c, g, t + i * 0.08, { wave: "square", from: f, to: f, duration: 0.16, gain: 0.16 * gain }),
        );
        break;
      case "select":
        tone(c, g, t, { wave: "square", from: 660, to: 880, duration: 0.06, gain: 0.1 * gain });
        break;
    }
  }

  /**
   * The engine. `load` is 0 idling to 1 flat out. If a recorded loop is in,
   * it runs underneath and speeds up with the throttle; the drone follows in
   * pitch and volume either way, so the tank sounds like it is working
   * without anything having to trigger a sample.
   */
  engine(load: number): void {
    const io = this.quiet ? null : this.out();
    if (!io) {
      this.stopEngine();
      return;
    }
    const { c, g } = io;
    if (!this.engineGain) {
      const gain = c.createGain();
      gain.gain.value = 0;
      const filter = c.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 620;
      filter.Q.value = 3;
      const osc = c.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = 46;
      const sub = c.createOscillator();
      sub.type = "square";
      sub.frequency.value = 23;
      osc.connect(filter);
      sub.connect(filter);
      filter.connect(gain);
      gain.connect(g);
      osc.start();
      sub.start();
      this.engineGain = gain;
      this.engineOsc = osc;
      this.engineSub = sub;
    }
    const loop = samples.get("engine");
    if (loop && !this.engineLoop) {
      const src = c.createBufferSource();
      src.buffer = loop;
      src.loop = true;
      const vol = c.createGain();
      vol.gain.value = 0;
      src.connect(vol);
      vol.connect(g);
      src.start();
      this.engineLoop = src;
      this.engineLoopGain = vol;
    }
    const k = Math.min(1, Math.max(0, load));
    const now = c.currentTime;
    const drone = this.engineLoop ? 0.45 : 1;
    this.engineGain.gain.setTargetAtTime((0.05 + k * 0.11) * drone, now, 0.12);
    this.engineOsc?.frequency.setTargetAtTime(44 + k * 52, now, 0.1);
    this.engineSub?.frequency.setTargetAtTime(22 + k * 26, now, 0.1);
    this.engineLoopGain?.gain.setTargetAtTime(0.12 + k * 0.28, now, 0.15);
    this.engineLoop?.playbackRate.setTargetAtTime(0.85 + k * 0.5, now, 0.2);
  }

  stopEngine(): void {
    this.engineOsc?.stop();
    this.engineSub?.stop();
    this.engineGain?.disconnect();
    this.engineOsc = null;
    this.engineSub = null;
    this.engineGain = null;
    try {
      this.engineLoop?.stop();
    } catch {
      // Already stopped.
    }
    this.engineLoopGain?.disconnect();
    this.engineLoop = null;
    this.engineLoopGain = null;
  }

  /** The page is going away. Silence, but leave the context for the next build. */
  dispose(): void {
    this.stopped = true;
    this.musicWanted = null;
    this.stopEngine();
    this.stopMusic();
  }
}

// ---- the two shapes every effect is made of --------------------------------

type ToneSpec = {
  wave: OscillatorType;
  from: number;
  to: number;
  duration: number;
  gain: number;
};

function tone(c: AudioContext, out: GainNode, at: number, s: ToneSpec): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = s.wave;
  osc.frequency.setValueAtTime(s.from, at);
  osc.frequency.exponentialRampToValueAtTime(Math.max(12, s.to), at + s.duration);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, s.gain), at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + s.duration);
  osc.connect(gain);
  gain.connect(out);
  osc.start(at);
  osc.stop(at + s.duration + 0.02);
}

type BurstSpec = {
  duration: number;
  type: BiquadFilterType;
  from: number;
  to: number;
  gain: number;
  q?: number;
};

function burst(c: AudioContext, out: GainNode, at: number, s: BurstSpec): void {
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c);
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = s.type;
  filter.frequency.setValueAtTime(s.from, at);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, s.to), at + s.duration);
  if (s.q) filter.Q.value = s.q;
  const gain = c.createGain();
  gain.gain.setValueAtTime(s.gain, at);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + s.duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  src.start(at);
  src.stop(at + s.duration + 0.02);
}

// ---- the switches, remembered ----------------------------------------------

/** Sliders feel even when the gain follows the square of where they sit. */
const curve = (v: number) => v * v;
const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

const MUSIC_LEVEL = "paper-tank.music-volume";
const SFX_LEVEL = "paper-tank.sfx-volume";

function readLevel(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : clamp01(Number(raw));
  } catch {
    return fallback;
  }
}

function writeLevel(key: string, v: number): void {
  try {
    localStorage.setItem(key, String(Math.round(v * 100) / 100));
  } catch {
    // No store.
  }
}

/** The old music switch, read once so "off" carries over to a zero slider. */
const MUSIC_STORE = "paper-tank.music";

function readMusic(): boolean {
  try {
    return localStorage.getItem(MUSIC_STORE) !== "off";
  } catch {
    return true;
  }
}

function read(): boolean {
  try {
    return localStorage.getItem(STORE) === "off";
  } catch {
    return false;
  }
}

function write(muted: boolean): void {
  try {
    if (muted) localStorage.setItem(STORE, "off");
    else localStorage.removeItem(STORE);
  } catch {
    // No store. The switch still holds for this visit.
  }
}
